import _SessionProviderEndSessionMutation from "../__generated__/SessionProviderEndSessionMutation.graphql";
import _SessionProviderRefreshAudioTokenMutation from "../__generated__/SessionProviderRefreshAudioTokenMutation.graphql";
import _SessionProviderStartSessionMutation from "../__generated__/SessionProviderStartSessionMutation.graphql";
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { BargeIn } from '../audio/bargein';
import { AudioGraph } from '../audio/graph';
import { SessionMicrophone } from '../audio/micPolicy';
import { closeMicrophone, openMicrophone } from '../audio/microphone';
import { PlaybackQueue } from '../audio/playback';
import { AudioTransport } from '../audio/transport';
import { MeQuery } from '../components/guards';
import { t } from '../i18n/t';
import { sessionInput } from '../views/topics';
import { SessionContext, type SessionContextValue, type SessionCredentials, type StartResult } from './context';
import { whyNotStarted } from './refusals.ts';
import { ServerEnd } from './serverEnd.ts';
import { useLiveSession } from './live';
import type { guardsMeQuery } from '../__generated__/guardsMeQuery.graphql';
import type { SessionProviderEndSessionMutation } from '../__generated__/SessionProviderEndSessionMutation.graphql';
import type { SessionProviderRefreshAudioTokenMutation } from '../__generated__/SessionProviderRefreshAudioTokenMutation.graphql';
import type { SessionProviderStartSessionMutation } from '../__generated__/SessionProviderStartSessionMutation.graphql';

// The two payload selections below must stay identical: `connect` reads one
// shape and does not care which mutation produced it, which is the whole
// reason a resume needs no second code path. `refreshAudioToken` returns
// `StartSessionPayload` for exactly that reason -- see `mutation.rs`.
const StartSession = (_SessionProviderStartSessionMutation.hash && _SessionProviderStartSessionMutation.hash !== "e29233852e019c90e86ef822616ca554" && console.error("The definition of 'SessionProviderStartSessionMutation' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _SessionProviderStartSessionMutation);
const RefreshAudioToken = (_SessionProviderRefreshAudioTokenMutation.hash && _SessionProviderRefreshAudioTokenMutation.hash !== "dd1756a27dfb87e7517fd5ac55861432" && console.error("The definition of 'SessionProviderRefreshAudioTokenMutation' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _SessionProviderRefreshAudioTokenMutation);
const EndSession = (_SessionProviderEndSessionMutation.hash && _SessionProviderEndSessionMutation.hash !== "74b3a8bdeb1fa60a9ebb7518d3449b1b" && console.error("The definition of 'SessionProviderEndSessionMutation' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _SessionProviderEndSessionMutation);

/**
 * The `/session` layout route: session identity, credentials, and the audio
 * machine, for every screen underneath.
 *
 * It is a layout route rather than state inside `SessionView` because the
 * start screen and the conversation are now two routes, and the audio machine
 * has to survive the navigation between them. `startSession` answers while the
 * start screen is still mounted; without a provider above both, the
 * `audioWsUrl` and single-use `audioToken` it returns would be thrown away at
 * the navigation and have to be re-minted a route later.
 *
 * `connect` is the only thing that builds the machine, and both mutations feed
 * it. That is the invariant worth protecting: a resumed session and a fresh one
 * are indistinguishable below this line.
 */
export function SessionProvider() {
  // The query the guards above already ran, so Relay serves it from the store
  // rather than over the network. `RequireAccount` has already refused every
  // caller for whom `learner` is null.
  const me = useLazyLoadQuery<guardsMeQuery>(MeQuery, {});
  const learnerId = me.me!.learner!.id;
  const [startSession] = useMutation<SessionProviderStartSessionMutation>(StartSession);
  const [refreshAudioToken] = useMutation<SessionProviderRefreshAudioTokenMutation>(RefreshAudioToken);
  const [endSession] = useMutation<SessionProviderEndSessionMutation>(EndSession);
  const [credentials, setCredentials] = useState<SessionCredentials | null>(null);
  const [ready, setReady] = useState(false);
  const [dead, setDead] = useState<string | null>(null);
  const [autoCapturing, setAutoCapturing] = useState(false);
  const [micError, setMicError] = useState<string | null>(null);
  const [micOpening, setMicOpening] = useState(false);
  const [micAllowed, setMicAllowed] = useState(false);
  const [ended, setEnded] = useState(false);
  const [closing, setClosing] = useState(false);
  const [turnsEnded, setTurnsEnded] = useState(0);
  const [replayable, setReplayable] = useState(false);
  const [replaying, setReplaying] = useState(false);
  const graph = useRef<AudioGraph | null>(null);
  const mic = useRef<SessionMicrophone | null>(null);
  const transport = useRef<AudioTransport | null>(null);
  const playback = useRef<PlaybackQueue | null>(null);
  const bargeIn = useRef<BargeIn | null>(null);
  const serverEnd = useRef<ServerEnd>(new ServerEnd());

  // Which session the machine is attached to, or is attaching to. Written
  // before the mutation goes out rather than after it answers, so a double
  // invocation -- StrictMode's second effect pass, a re-render mid-flight --
  // cannot mint two single-use tokens for one session and race two sockets.
  const attached = useRef<string | null>(null);

  // This tab's End, so the `SessionEnded` it causes is not read as the
  // server closing a talk: that would put "Saying goodbye…" on an adult's
  // button for the round trip the mutation takes.
  const endRequested = useRef(false);
  const die = useCallback((why: string) => setDead(d => d ?? why), []);

  // The microphone first, so its stream is released before the graph it
  // feeds closes under it.
  const stopCapture = useCallback(() => {
    mic.current?.stop();
    graph.current?.stop();
    mic.current = null;
    graph.current = null;
  }, []);
  const teardown = useCallback(() => {
    stopCapture();
    // `release`, not a bare close: the close event lands a round trip later,
    // after `connect` has cleared `dead` for the next session.
    transport.current?.release();
    bargeIn.current?.stop();
    playback.current?.stop();
    transport.current = null;
    bargeIn.current = null;
    playback.current = null;
  }, [stopCapture]);

  /**
   * Build the audio machine for a session and publish it.
   *
   * Lifted verbatim from `SessionView.begin`'s `onCompleted`, wiring included:
   * the queue is built before the `BargeIn` that listens to it, so the
   * callbacks are ref reads rather than captured values.
   */
  const connect = useCallback((creds: SessionCredentials) => {
    // The provider outlives any one session -- ending a conversation and
    // starting another never unmounts it -- so a previous machine has to go
    // before a new one is wired, and the flags that described the old
    // session have to stop describing the new one.
    teardown();
    attached.current = creds.sessionId;
    endRequested.current = false;
    setReady(false);
    setDead(null);
    setMicError(null);
    setMicOpening(false);
    setMicAllowed(false);
    setAutoCapturing(false);
    setEnded(false);
    setClosing(false);
    setTurnsEnded(0);
    setReplayable(false);
    setReplaying(false);
    serverEnd.current = new ServerEnd();
    setCredentials(creds);

    // The callback is a ref read for the same reason `BargeInPorts` are:
    // the queue is built before the `BargeIn` that listens to it.
    playback.current = new PlaybackQueue(() => {
      bargeIn.current?.playbackDrained();
      setReplayable(playback.current?.hasReply() ?? false);
      if (serverEnd.current.drained()) setEnded(true);
    }, () => {
      bargeIn.current?.replayEnded();
      setReplaying(false);
    });
    const ws = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}${creds.audioWsUrl}`;
    // Every frame, not just `chunk`: `PlaybackQueue` owns the turn boundary,
    // and it learns about it from `done`.
    transport.current = new AudioTransport(ws, creds.audioToken, frame => {
      // First, so a reply's first chunk makes `BargeIn` a reply's before
      // the queue stops the replay it takes over from: the replay's end
      // is then a no-op rather than a flicker of the flag.
      bargeIn.current?.onFrame(frame);
      // The same frames, read for a different fact: whether the tutor is
      // currently audible. See `BargeIn`.
      playback.current?.onFrame(frame);
      // A reply on its way is no moment to replay the last one (§3).
      if (frame.kind === 'chunk') setReplayable(false);
      // And a third: that a turn is over. This connection is the only
      // plane that cannot miss that -- it is held from before the turn
      // begins -- so it is what a screen waiting on a turn it never saw
      // start has to wait on. See `turnsEnded`.
      if (frame.kind === 'done') setTurnsEnded(n => n + 1);
    }, () => {
      bargeIn.current?.stop();
      // Detached as well as dead, so `resume` for the same id attaches
      // again: without this it returned early for ever, and "Start again"
      // looped back to "Disconnected" until a reload.
      attached.current = null;
      die(t('session-dropped-audio'));
    });
    graph.current = new AudioGraph({
      onPcm: pcm => transport.current?.sendPcm(pcm),
      onEnded: () => {
        transport.current?.endUtterance();
        setAutoCapturing(false);
        mic.current?.ended();
      },
      // Only hands-free's own transitions send "started" -- see the
      // worklet's `process`. A manual press reaches `captureStarted`
      // through `beginUtterance` below instead.
      onStarted: () => {
        // Between an utterance's end and the server's `Transcribing` no
        // state says a turn is on its way; the next `onSilent` restores it.
        setReplayable(false);
        bargeIn.current?.captureStarted();
        setAutoCapturing(true);
        mic.current?.started();
      }
    });
    mic.current = new SessionMicrophone(graph.current, () => openMicrophone(), closeMicrophone, {
      opening: setMicOpening,
      refused: e => setMicError(String(e)),
      releasedBeforeOpen: () => setMicAllowed(true),
      opened: () => setMicError(null)
    });
    // Last, but its ports are ref reads -- see `BargeInPorts`.
    bargeIn.current = new BargeIn({
      tellWorklet: on => graph.current?.setTutorSpeaking(on),
      cancel: playedThrough => transport.current?.cancel(playedThrough),
      lastPlayed: () => playback.current?.lastPlayedSentence() ?? null,
      flush: () => playback.current?.flush(),
      stopReplay: () => playback.current?.stopReplay()
    });
    // Each answer only for its own graph: one torn down by a quick restart
    // must not mark the next session ready, or put its error on it.
    const g = graph.current;
    g.start().then(() => {
      if (graph.current === g) setReady(true);
    })
    // A graph that cannot be built (a 44.1 kHz device) is reported where a
    // refused microphone is, rather than leaving "preparing" for ever.
    .catch((e: unknown) => {
      if (graph.current === g) setMicError(String(e));
    });
  }, [die, teardown]);
  const start = useCallback((topic?: string) => new Promise<StartResult>(resolve => {
    startSession({
      // The child's offset from UTC, for Story time's good night (kid
      // engagement PRD, F1).
      variables: {
        input: {
          ...sessionInput(learnerId, topic),
          utcOffsetMinutes: -new Date().getTimezoneOffset()
        }
      },
      onCompleted: s => {
        const {
          session,
          audioWsUrl,
          audioToken
        } = s.startSession;
        if (!session.id) {
          resolve({
            why: t(whyNotStarted(null))
          });
          return;
        }
        connect({
          sessionId: session.id,
          audioWsUrl,
          audioToken,
          character: session.character ?? null,
          status: session.status,
          topic: session.topic ?? null,
          storyTitle: session.storyTitle ?? null
        });
        resolve({
          sessionId: session.id
        });
      },
      // Not `die`: a refused start is not a dead conversation, and its
      // reason belongs on the start screen that asked, in the learner's
      // words rather than the server's (`refusals.ts`).
      onError: e => resolve({
        why: t(whyNotStarted(e))
      })
    });
  }), [startSession, learnerId, connect]);
  const resume = useCallback((sessionId: string) => {
    if (attached.current === sessionId) return;
    attached.current = sessionId;
    refreshAudioToken({
      variables: {
        sessionId
      },
      onCompleted: s => {
        const {
          session,
          audioWsUrl,
          audioToken
        } = s.refreshAudioToken;
        connect({
          sessionId: session.id,
          audioWsUrl,
          audioToken,
          character: session.character ?? null,
          status: session.status,
          topic: session.topic ?? null,
          storyTitle: session.storyTitle ?? null
        });
      },
      // `refreshAudioToken` refuses anything that is not active, so this is
      // the ordinary answer for a link to a session that has since ended --
      // not only a transport failure. `dead` is the honest state for both:
      // the screen below still renders the transcript and the debrief, it
      // just cannot be talked to.
      onError: e => die(e.message)
    });
  }, [refreshAudioToken, connect, die]);
  const end = useCallback(() => {
    const sessionId = attached.current;
    if (!sessionId) return;
    endRequested.current = true;
    playback.current?.stop();
    bargeIn.current?.stop();
    // Before the mutation, not at `ended`: the server closes the socket as
    // soon as the session ends, and a hands-free microphone would otherwise
    // be streaming into that close. The close that follows is this press,
    // whatever code it arrives with.
    stopCapture();
    transport.current?.expectClose();
    endSession({
      variables: {
        sessionId
      },
      onCompleted: () => setEnded(true),
      // The microphone is already off, so a talk that could not be ended
      // cannot be carried on either, and saying nothing would leave a talk
      // button that records into nothing. Detached as `onDead` detaches, so
      // "Reconnect" reaches the still-active session through `resume`, and
      // released so the open socket does not hold its permit until the idle
      // timeout.
      onError: e => {
        console.error('endSession failed', e);
        attached.current = null;
        transport.current?.release();
        transport.current = null;
        die(t('session-end-failed'));
      }
    });
  }, [endSession, die, stopCapture]);
  const serverEnded = useCallback(() => {
    // Nothing the learner says now can reach the session, so the microphone
    // goes before the goodbye has finished; the ending waits for the audio.
    stopCapture();
    // Barge-in goes with it. A press on the talk button still reaches
    // `captureStarted` until `ended`, and its flush silences the goodbye
    // without the drain `ServerEnd` is waiting for: no celebration, ever.
    bargeIn.current?.stop();
    bargeIn.current = null;
    // A replay is no turn, so nothing else would stop it, and the ending waits
    // on a silent queue.
    playback.current?.stopReplay();
    // And the button says so, rather than "I'm listening" under a press.
    // Also set when another tab ends the session mid-reply: the button is
    // then waiting on the end of a reply, not a goodbye, and says the same.
    if (!endRequested.current) setClosing(true);
    if (serverEnd.current.announce(playback.current?.isSilent() ?? true)) setEnded(true);
  }, [stopCapture]);

  // A press over the tutor is barge-in, exactly as hands-free's onset is --
  // see `BargeIn.captureStarted`, a no-op while the tutor is silent. At the
  // press, before the microphone opens, so the tutor stops the moment the
  // learner asks to speak and the `CANCEL` precedes every byte captured.
  const beginUtterance = useCallback(() => {
    setReplayable(false);
    bargeIn.current?.captureStarted();
    setMicAllowed(false);
    mic.current?.press();
  }, []);
  const endUtterance = useCallback(() => mic.current?.release(), []);
  const setHandsFree = useCallback((on: boolean) => mic.current?.setHandsFree(on), []);
  const hearAgain = useCallback(() => {
    if (!playback.current?.replay()) return;
    bargeIn.current?.replayStarted();
    setReplaying(true);
  }, []);
  useEffect(() => () => teardown(), [teardown]);

  // The microphone indicator staying lit for a session that can no longer send
  // a byte is the same lie the button was telling.
  useEffect(() => {
    if (dead || ended) stopCapture();
  }, [dead, ended, stopCapture]);

  // A finished session gives its socket back, and with it the server's
  // live-session slot (phase 2 fix round 1). `ended` is set only once a
  // server-ended goodbye has drained, and after `end` has stopped playback,
  // so nothing still to be heard is cut. The server closes its side too; this
  // is the half that does not wait for it.
  useEffect(() => {
    if (!ended) return;
    bargeIn.current?.stop();
    bargeIn.current = null;
    transport.current?.release();
    transport.current = null;
  }, [ended]);
  const liveSession = useLiveSession();
  useEffect(() => {
    // Registered while a conversation is running and cleared when it is not,
    // so the navs ask before interrupting it and call `endSession` rather than
    // letting the socket close on an unmount nobody recorded.
    liveSession.set({
      live: credentials !== null && !ended && dead === null,
      end: async () => {
        end();
      }
    });
    return () => liveSession.set({
      live: false,
      end: async () => {}
    });
    // `liveSession` is a fresh object each render; depending on it would loop.
  }, [credentials, ended, dead, end]);
  const value = useMemo<SessionContextValue>(() => ({
    credentials,
    ready,
    dead,
    autoCapturing,
    micError,
    micOpening,
    micAllowed,
    ended,
    closing,
    turnsEnded,
    replayable,
    replaying,
    hearAgain,
    start,
    resume,
    end,
    die,
    serverEnded,
    beginUtterance,
    endUtterance,
    setHandsFree
  }), [credentials, ready, dead, autoCapturing, micError, micOpening, micAllowed, ended, closing, turnsEnded, replayable, replaying, hearAgain, start, resume, end, die, serverEnded, beginUtterance, endUtterance, setHandsFree]);
  return <SessionContext.Provider value={value}>
      {/* The boundary belongs *below* this provider, not above it.
          `AppLayout`'s is above, and a child suspending -- `SessionGate`'s
          `network-only` read, `SessionView`'s replay of the stored turns --
          would suspend this component along with it, putting a connected
          socket and a running `AudioContext` inside a subtree React is free
          to discard. Caught here, a suspend below never reaches the audio
          machine at all.
           Same fallback as the shell's, because it replaces the same panel. */}
      <Suspense fallback={<p role="status" className="text-base-content/60 py-8 text-sm">
            {t('common-loading')}
          </p>}>
        <Outlet />
      </Suspense>
    </SessionContext.Provider>;
}